import React, { ReactNode, useCallback, useEffect, useState } from 'react';

import { useFetch } from '@uservoice/rest-utils';
import { AxiosError } from 'axios';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';

import { GetUsersParams, getUsers, User } from '@/core/api/collections/users';
import { useDebounce } from '@/core/hooks/useDebounce';

export interface UserSearchCtx {
  canSort: boolean,
  emailFilter: string;
  error?: AxiosError;
  fetchMoreIfReady: () => void;
  hasMore: boolean;
  isInitialLoad: boolean;
  loading: boolean;
  setEmailFilter: React.Dispatch<React.SetStateAction<string>>;
  setSort: (newValue: string) => void;
  sort: string;
  users?: User[];
}

export const UserSearchContext = React.createContext<UserSearchCtx>({} as UserSearchCtx);

type Props = {
  children: ReactNode;
}
export const UserSearchContextProvider = ({ children }: Props) => {
  const [sort, setSort] = useQueryParam('sort', withDefault(StringParam, 'display_name'));
  const [canSort, setCanSort] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [emailFilter, setEmailFilter] = useState('');
  const debouncedEmailFilter = useDebounce(emailFilter, 800);

  const [users, loading, error, fetchUsers, hasMore, fetchMore] = useFetch<GetUsersParams, User[]>(getUsers, {
    sort,
    query: debouncedEmailFilter,
  }, { lazyFetch: true, onCompleted: () => setIsInitialLoad(false) });

  useEffect(() => {
    setCanSort(!debouncedEmailFilter.length);
    if (debouncedEmailFilter.length) {
      setSort(undefined);
    }
    fetchUsers();
  }, [sort, debouncedEmailFilter]);

  const fetchMoreIfReady = useCallback(() => {
    if (!loading) {
      fetchMore();
    }
  }, [loading]);

  return (
    <UserSearchContext.Provider
      value={{
        canSort,
        emailFilter,
        error,
        fetchMoreIfReady,
        hasMore,
        isInitialLoad,
        loading,
        setEmailFilter,
        setSort,
        sort,
        users,
      }}
    >
      {children}
    </UserSearchContext.Provider>
  );
};

